import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  DashboardDataTableScroll,
  DashboardDataTableTable,
  DashboardSectionHeader,
  dashboardDataTable as dt,
} from "src/components/common";
import RestaurantAiExecutiveSummary from "../RestaurantAiExecutiveSummary";
import RestaurantHeaderActions, { type RestaurantHeaderActionsProps } from "../RestaurantHeaderActions";
import RestaurantKpiCard from "../RestaurantKpiCard";
import RestaurantSectionCard from "../RestaurantSectionCard";
import RestaurantDonutBreakdown from "../charts/RestaurantDonutBreakdown";
import RestaurantCategoryBars from "../charts/RestaurantCategoryBars";
import RestaurantHorizontalBars from "../charts/RestaurantHorizontalBars";
import RestaurantAbandonedCartAnalytics from "../RestaurantAbandonedCartAnalytics";
import { useRestaurantFormatters } from "../../utils/restaurantFormatters";
import type { RestaurantFilters, RestaurantMetrics } from "../../utils/restaurantMetrics";

const CHANNEL_LABEL_KEYS: Record<string, string> = {
  mobile_app: "restaurantAnalytics.channels.mobileApp",
  website: "restaurantAnalytics.channels.website",
  kiosk: "restaurantAnalytics.channels.kiosk",
  pos: "restaurantAnalytics.channels.pos",
  call_center: "restaurantAnalytics.channels.callCenter",
  aggregator: "restaurantAnalytics.channels.aggregator",
};

const SEGMENT_LABEL_KEYS: Record<string, string> = {
  new: "restaurantAnalytics.segments.new",
  returning: "restaurantAnalytics.segments.returning",
  loyal: "restaurantAnalytics.segments.loyal",
  at_risk: "restaurantAnalytics.segments.atRisk",
};

interface RestaurantChannelsCustomersTabProps {
  metrics: RestaurantMetrics;
  filters: RestaurantFilters;
  headerProps: RestaurantHeaderActionsProps;
}

const RestaurantChannelsCustomersTab: FC<RestaurantChannelsCustomersTabProps> = ({ metrics, filters, headerProps }) => {
  const { t } = useTranslation();
  const fmt = useRestaurantFormatters();

  /** Cohort ids are "YYYY-MM"; render them as a localized month + year. */
  const formatCohortMonth = (cohort: string) => {
    const parsed = new Date(`${cohort}-01T00:00:00Z`);
    if (Number.isNaN(parsed.getTime())) {
      return cohort;
    }
    return new Intl.DateTimeFormat(fmt.isRTL ? "ar-EG-u-nu-arab" : "en-US", {
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(parsed);
  };

  const channelDonutData = metrics.channels.map(channel => ({
    id: channel.id,
    label: t(CHANNEL_LABEL_KEYS[channel.id] ?? channel.id, { defaultValue: channel.id }),
    orders: channel.orders,
    revenue: channel.revenue,
    share: channel.share,
  }));

  const segmentBars = metrics.segments.map(segment => ({
    id: segment.id,
    label: t(SEGMENT_LABEL_KEYS[segment.id] ?? segment.id, { defaultValue: segment.id }),
    value: segment.customers,
  }));

  // Sorted by value so the bars read as a ranking, matching the other charts.
  const channelAovBars = metrics.channels
    .map(channel => ({
      id: channel.id,
      label: t(CHANNEL_LABEL_KEYS[channel.id] ?? channel.id, { defaultValue: channel.id }),
      value: channel.aov,
    }))
    .sort((a, b) => b.value - a.value);

  const maxCohortMonths = useMemo(
    () => Math.max(...metrics.retention.map(c => c.retention.length)),
    [metrics.retention],
  );

  return (
    <div className="restaurant-analytics-dashboard__section">
      <DashboardSectionHeader
        title={t("restaurantAnalytics.tabs.channels", { defaultValue: "Channels & Customers" })}
        subtitle={t("restaurantAnalytics.header.channelsSubtitle", {
          defaultValue: "Channel mix, customer segments, and retention.",
        })}
        sticky
        actions={<RestaurantHeaderActions {...headerProps} />}
      />

      <RestaurantAiExecutiveSummary tab="channels-customers" metrics={metrics} filters={filters} />

      <div className="restaurant-analytics-dashboard__kpi-row">
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.uniqueCustomers", { defaultValue: "Unique customers" })}
          value={fmt.formatNumber(metrics.customers)}
          delta={metrics.customersDelta}
          accent="primary"
          tooltip={t("restaurantAnalytics.kpiTooltips.uniqueCustomers", { defaultValue: "Number of distinct customers who placed at least one order." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.newCustomers", { defaultValue: "New customers" })}
          value={fmt.formatNumber(metrics.newCustomers)}
          delta={metrics.newCustomersDelta}
          accent="success"
          tooltip={t("restaurantAnalytics.kpiTooltips.newCustomers", { defaultValue: "Customers who placed their first order in the period." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.repeatOrderRate", { defaultValue: "Repeat order rate" })}
          value={fmt.formatPercent(metrics.repeatRate)}
          delta={metrics.repeatRateDelta}
          accent="warning"
          tooltip={t("restaurantAnalytics.kpiTooltips.repeatOrderRate", { defaultValue: "Share of customers who placed more than one order in the period." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.avgOrderValue", { defaultValue: "Average order value" })}
          value={fmt.formatCurrencyAverage(metrics.aov)}
          delta={metrics.aovDelta}
          accent="primary"
          tooltipAlign="end"
          tooltip={t("restaurantAnalytics.kpiTooltips.avgOrderValue", { defaultValue: "Total revenue divided by total orders." })}
        />
      </div>

      <div className="restaurant-analytics-dashboard__grid restaurant-analytics-dashboard__grid--two">
        <RestaurantDonutBreakdown
          title={t("restaurantAnalytics.charts.revenueByChannel", { defaultValue: "Revenue by channel" })}
          tip={t("restaurantAnalytics.chartTips.revenueByChannel", { defaultValue: "Revenue split across app, website, POS, kiosk, call centre, and aggregators." })}
          data={channelDonutData}
          metric="revenue"
        />
        <RestaurantCategoryBars
          title={t("restaurantAnalytics.charts.customerSegments", { defaultValue: "Customer segments" })}
          tip={t("restaurantAnalytics.chartTips.customerSegments", { defaultValue: "Customer counts by segment — new, returning, loyal, and at risk." })}
          data={segmentBars}
          valueKind="number"
          colorIndex={2}
        />
      </div>

      <div className="restaurant-analytics-dashboard__grid restaurant-analytics-dashboard__grid--two">
        <RestaurantHorizontalBars
          title={t("restaurantAnalytics.charts.channelPerformance", { defaultValue: "Channel performance" })}
          tip={t("restaurantAnalytics.chartTips.channelPerformance", { defaultValue: "Average order value achieved by each ordering channel." })}
          data={channelAovBars}
          valueKind="currency"
        />

        <RestaurantSectionCard
          title={t("restaurantAnalytics.charts.retentionCohorts", { defaultValue: "Retention cohorts" })}
          tip={t("restaurantAnalytics.chartTips.retentionCohorts", { defaultValue: "Share of each monthly signup cohort still ordering in later months." })}
          bodyClassName="restaurant-table-body"
        >
          <div className="restaurant-table-scroll">
            <table className="restaurant-retention-table">
              <thead>
                <tr>
                  <th>{t("restaurantAnalytics.table.cohort", { defaultValue: "Cohort" })}</th>
                  {Array.from({ length: maxCohortMonths }).map((_, index) => (
                    <th key={index} className="restaurant-retention-table__month-heading">
                      {t("restaurantAnalytics.table.month", { defaultValue: "Month {{index}}", index })}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {metrics.retention.map(cohort => (
                  <tr key={cohort.cohort}>
                    <td className="restaurant-retention-table__cohort">
                      {`${formatCohortMonth(cohort.cohort)} · ${fmt.formatNumber(cohort.size)}`}
                    </td>
                    {Array.from({ length: maxCohortMonths }).map((_, index) => {
                      const value = cohort.retention[index];
                      if (value === undefined) {
                        return <td key={index} />;
                      }
                      return (
                        <td
                          key={index}
                          className="restaurant-retention-table__cell"
                          style={{
                            backgroundColor: `color-mix(in srgb, #2c68ad ${Math.round(value * 90)}%, #eef1f5)`,
                            color: value > 0.6 ? "#ffffff" : "#1a1a1a",
                          }}
                        >
                          {fmt.formatPercent(value, 0)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </RestaurantSectionCard>
      </div>

      <RestaurantSectionCard
        title={t("restaurantAnalytics.charts.customerSegmentsTable", { defaultValue: "Customer segments" })}
        tip={t("restaurantAnalytics.chartTips.customerSegmentsTable", { defaultValue: "Customer count, revenue, and orders per customer for each segment." })}
        bodyClassName="restaurant-table-body"
      >
        <DashboardDataTableScroll>
          <DashboardDataTableTable className={`${dt.tableCompact} restaurant-wide-table`}>
            <thead>
              <tr>
                <th>{t("restaurantAnalytics.table.segment", { defaultValue: "Segment" })}</th>
                <th className={dt.number}>{t("restaurantAnalytics.table.customers", { defaultValue: "Customers" })}</th>
                <th className={dt.number}>{t("restaurantAnalytics.table.revenue", { defaultValue: "Revenue" })}</th>
                <th className={dt.number}>{t("restaurantAnalytics.table.ordersPerCustomer", { defaultValue: "Avg. orders per customer" })}</th>
              </tr>
            </thead>
            <tbody>
              {metrics.segments.map(segment => (
                <tr key={segment.id}>
                  <td className={dt.cellEmphasis}>
                    {t(SEGMENT_LABEL_KEYS[segment.id] ?? segment.id, { defaultValue: segment.id })}
                  </td>
                  <td className={dt.number}>{fmt.formatNumber(segment.customers)}</td>
                  <td className={dt.number}>{fmt.formatCurrency(segment.revenue, { compact: true })}</td>
                  <td className={dt.number}>{fmt.formatNumber(segment.ordersPerCustomer, 1)}</td>
                </tr>
              ))}
            </tbody>
          </DashboardDataTableTable>
        </DashboardDataTableScroll>
      </RestaurantSectionCard>

      <RestaurantAbandonedCartAnalytics metrics={metrics} showKpis={false} />
    </div>
  );
};

export default RestaurantChannelsCustomersTab;
